/**
 * Defines the Calendar elapsed time view. This is a component composed of H, M, and S fields with rollover capabilities.
 *
 * Model: ElapsedTime
 */
import _ from 'underscore';

import Backbone from 'backbone';
import Localizer from 'localizer';
import Personalizer from 'personalizer';
import ElapsedTimeValidator from 'utils/validators/ElapsedTimeValidator';
import ToolTipErrorReporter from 'utils/reporters/ToolTipErrorReporter';
import ErrorToolTipView from 'views/utils/ErrorToolTipView';
import templateHtml from 'templates/utils/ElapsedTimeView.html';

var Events = {
    CHANGE: 'CHANGE'
};

var ElapsedTimeView = Backbone.View.extend({
    initialize: function(args) {
        if (!args.idPrefix) {
            throw 'A DOM ID Prefix is required for ElapsedTimeView.';
        }

        if (!args.maxExceededErrorKey) {
            throw 'An error key is required for ElapsedTimeView.';
        }

        this.scrollableElement = args.scrollableElement; // Needed for validatoin if the element is within a scrollable area
        this.tooltipContainer = args.tooltipContainer; // set the tooltip withing an area container
        this.hideSeconds = args.hideSeconds || false;
        this.hideHours = args.hideHours || false;
        this.hideMilliseconds = _.isUndefined(args.hideMilliseconds) ? true : args.hideMilliseconds;
        this.idPrefix = args.idPrefix;
        this.maxExceededErrorKey = args.maxExceededErrorKey;
        this.underMinimumTimeErrorKey = args.underMinimumTimeErrorKey
            ? args.underMinimumTimeErrorKey
            : 'time_under_minimum_allowed';

        if (args.value) {
            this.setHoursMinutesSeconds(args.value);
        } else {
            this.hours = 0;
            this.minutes = 0;
            this.seconds = 0;
            this.milliseconds = 0;
        }

        this.maxTime = args.maxTime ? args.maxTime : this.getDefaultMaxTime();
        this.minTime = args.minTime ? args.minTime : 0;
        this.hourInputMaxLength = args.hourInputMaxLength ? args.hourInputMaxLength : 3;
    },

    getDefaultMaxTime() {
        // No hours, so max time is less
        if (this.hideHours) {
            return 60 * 60 - 1;
        }
        return 500 * 60 * 60; // 500 max hours
    },

    setHoursMinutesSeconds: function(value) {
        var hours = Math.floor(value / 3600);
        var minutes = Math.floor(value / 60 - hours * 60);
        var rawSeconds = value - hours * 3600 - minutes * 60;
        var seconds = Math.floor(rawSeconds);
        var deltaSeconds = Math.round((rawSeconds - seconds) * 10000) / 10000.0;
        var milliseconds = Math.round(deltaSeconds * 1000);

        var hasFractionalPart = deltaSeconds > 0;
        var showTenthsOfSeconds = hasFractionalPart && value < 599.95; /* 9:59.95 */

        // handle rounding up
        if (
            ((showTenthsOfSeconds && deltaSeconds >= 0.95) || (!showTenthsOfSeconds && deltaSeconds >= 0.5)) &&
            this.hideMilliseconds
        ) {
            seconds++;
            deltaSeconds = 0;
        }

        // handle rollover cases
        if (seconds > 59) {
            seconds = 0;
            minutes++;
            if (minutes > 59) {
                minutes = 0;
                hours++;
            }
        }

        if (showTenthsOfSeconds && this.hideMilliseconds) {
            seconds = Math.round(seconds + deltaSeconds);
        }

        this.hours = hours;
        this.minutes = minutes;
        this.seconds = seconds;
        this.milliseconds = milliseconds;
    },

    hourField: function() {
        return this.$('#' + this.idPrefix + '-time-hour');
    },

    minuteField: function() {
        return this.$('#' + this.idPrefix + '-time-minute');
    },

    secondField: function() {
        return this.$('#' + this.idPrefix + '-time-second');
    },

    millisecondField: function() {
        return this.$('#' + this.idPrefix + '-time-millisecond');
    },

    render: function() {
        var template = _.template(templateHtml);

        var model = {
            idPrefix: this.idPrefix,
            maxExceededErrorKey: this.maxExceededErrorKey,
            timeHours: this.hours,
            timeMinutes: this.minutes,
            timeSeconds: this.seconds,
            timeMilliseconds: this.milliseconds
        };

        this.html = template({
            model: model,
            Localizer: Localizer,
            hideHours: this.hideHours,
            hideSeconds: this.hideSeconds,
            hideMilliseconds: this.hideMilliseconds,
            hourInputMaxLength: this.hourInputMaxLength
        });
        this.$el.html(this.html + this.$el.html());

        var _this = this;
        this.validator = new ElapsedTimeValidator({
            maxTime: this.maxTime, // defaulted to 40 hours if no max time is given to the view as an argument
            minTime: this.minTime, // defaulted to 0 seconds if no min time is given to the view as an argument
            elements: [this.hourField(), this.minuteField(), this.secondField(), this.millisecondField()],
            reporter: new ToolTipErrorReporter({
                message: function(value, result) {
                    if (result === ElapsedTimeValidator.ValidationErrors.UnderMinTime) {
                        if (typeof _this.underMinimumTimeErrorKey === 'function') {
                            return _this.underMinimumTimeErrorKey();
                        } else {
                            return Localizer.localize(_this.underMinimumTimeErrorKey, value);
                        }
                    } else {
                        if (typeof _this.maxExceededErrorKey === 'function') {
                            return _this.maxExceededErrorKey();
                        } else {
                            return Localizer.localize(_this.maxExceededErrorKey, value);
                        }
                    }
                },
                scrollableElement: this.scrollableElement,
                position: 'top',
                element: this.hourField()[0] || this.minuteField()[0],
                container: this.tooltipContainer || null
            })
        });

        this.validator.on(ElapsedTimeValidator.Events.ValidationSuccess, this.onDurationInputValidationSucceeded, this);

        return this.el;
    },

    onDurationInputValidationSucceeded: function(result) {
        const currentTime = this.calculateTime();
        if (result.parsedValue !== currentTime) {
            this.setHoursMinutesSeconds(result.parsedValue);
            this.trigger(Events.CHANGE, this.calculateTime());
        }
    },

    calculateTime: function() {
        var hrs = this.hours;
        var mins = this.minutes;
        var secs = this.seconds;
        var milliseconds = this.milliseconds;

        var total = milliseconds / 1000.0 + 1 * secs + 60 * mins + 3600 * hrs;
        return total;
    }
});

ElapsedTimeView.Events = Events;

export default ElapsedTimeView;
